/**
 * Les Moves dans un VRAI exercice (et dans un défi) : le même comportement que la démo guidée, sans le
 * coach. PUR — la trousse, l'ordre de ses Moves, le geste à débloquer.
 *
 *  • Une CONNAISSANCE n'est proposée que si la feuille de la question accepte sa ligne (`shelfFitsSheet` :
 *    c'est un `pred` de la feuille). Tant qu'elle n'est pas acquise, ses voisines du niveau l'entourent —
 *    choisir la bonne EST l'exercice, donc aucune n'est mise en avant.
 *  • Un GESTE se débloque quand l'écriture touchée le permettrait (`gesturesInPlay`) : c'est le « nouveau
 *    geste », la trousse l'appelle et la main la montre. On l'explique, puis on s'en sert.
 *  • Une fois acquis, un Move reste dans la trousse : dans l'ordre le plus utile pour CETTE question
 *    (en jeu maintenant → connaissance de la question → autres gestes acquis). Les connaissances acquises
 *    qui ne servent pas ici se relisent dans l'onglet « Cours ».
 */
import type { SchoolLevel } from '@jeraisonne/db';
import {
  GESTURE_MOVES,
  MOVES,
  moveAvailableAt,
  shelfFitsSheet,
  type MoveDef,
  type MoveShelf,
} from './move-catalog';

export interface PlayerMoveEntry {
  move: MoveDef;
  /** déjà débloqué (sur cet appareil) */
  owned: boolean;
  /** la question le demande maintenant et il n'est pas acquis : la trousse l'appelle */
  needed: boolean;
  /** l'étagère ouverte quand on le touche */
  shelf: MoveShelf;
  /** l'onglet de la trousse : un Move utilisable, ou une fiche du cours à relire */
  tab: 'moves' | 'cours';
}

const SAVOIR_QUESTION = 'Quelle connaissance te manque ?';
const GESTE_QUESTION = 'Quel geste veux-tu faire ?';

/** Le Move est-il associé à l'une de ces SKILLCARDS ? PUR. */
function linkedTo(m: MoveDef, cardIds: readonly string[]): boolean {
  return cardIds.length > 0 && m.cardIds.some((id) => cardIds.includes(id));
}

function available(level: string | null | undefined, list: MoveDef[]): MoveDef[] {
  if (!level) return [];
  return list.filter((m) => moveAvailableAt(m, level as SchoolLevel));
}

/**
 * L'étagère des connaissances d'une question : celle que la feuille accepte, et deux voisines du niveau
 * (de vraies connaissances, qu'on débloquera ailleurs). null si la feuille n'en accepte aucune. PUR.
 */
export function savoirShelfForSheet(
  level: string | null | undefined,
  predLines: readonly string[],
  /** les SKILLCARDS de la question : une connaissance qui leur est associée passe devant */
  cardIds: readonly string[] = [],
): MoveShelf | null {
  const pool = available(level, MOVES);
  const fits = pool.filter((m) =>
    shelfFitsSheet({ question: '', moveId: m.id, alsoIds: [] }, predLines),
  );
  const fit = fits.find((m) => linkedTo(m, cardIds)) ?? fits[0];
  if (!fit) return null;
  // les voisines : du niveau même d'abord (une connaissance de 6e n'est pas un leurre en 3e)
  const exact = (m: MoveDef) => (level ? m.levels.includes(level as SchoolLevel) : false);
  const others = pool
    .filter((m) => m.id !== fit.id)
    .sort((a, b) => Number(exact(b)) - Number(exact(a)))
    .slice(0, 2)
    .map((m) => m.id);
  return { question: SAVOIR_QUESTION, moveId: fit.id, alsoIds: others };
}

/** L'étagère d'un geste : lui en vedette, les autres gestes du niveau autour. PUR. */
export function gestureShelf(level: string | null | undefined, moveId: string): MoveShelf {
  const others = available(level, GESTURE_MOVES)
    .filter((m) => m.id !== moveId)
    .map((m) => m.id);
  return { question: GESTE_QUESTION, moveId, alsoIds: others };
}

/**
 * Les Moves de la trousse pour CETTE question, dans l'ordre le plus pertinent. PUR.
 *
 *  1. les gestes en jeu (l'écriture touchée les permettrait) — à débloquer d'abord, puis acquis ;
 *  2. la connaissance de la question (et, tant qu'elle n'est pas acquise, ses voisines) ;
 *  3. les autres gestes acquis du niveau ;
 *  4. (onglet « Cours ») les connaissances acquises qui ne servent pas ici.
 *
 * Un geste verrouillé qui n'est PAS en jeu n'est pas montré : la trousse ne propose pas ce que la question
 * ne demande pas, il arrivera le jour où une écriture en aura besoin.
 */
export function playerMoveEntries(input: {
  level: string | null | undefined;
  predLines: readonly string[];
  unlocked: ReadonlySet<string>;
  inPlay: readonly string[];
  /** les SKILLCARDS de la question (un Move associé passe devant) */
  cardIds?: readonly string[];
}): PlayerMoveEntry[] {
  const { level, predLines, inPlay, cardIds = [] } = input;
  // un Move « débloqué par défaut » (réglage de l'admin) compte comme acquis
  const unlocked = new Set(input.unlocked);
  for (const m of [...MOVES, ...GESTURE_MOVES]) if (m.unlockedByDefault) unlocked.add(m.id);
  const out: PlayerMoveEntry[] = [];
  const seen = new Set<string>();
  const push = (e: PlayerMoveEntry) => {
    if (seen.has(e.move.id)) return;
    seen.add(e.move.id);
    out.push(e);
  };
  // à pertinence égale : les Moves associés aux SKILLCARDS de la question d'abord
  const gestures = available(level, GESTURE_MOVES).sort(
    (a, b) => Number(linkedTo(b, cardIds)) - Number(linkedTo(a, cardIds)),
  );
  const playing = new Set(inPlay);

  // 1. en jeu : le verrouillé d'abord (c'est lui que la main montre), puis les acquis
  const inPlayMoves = gestures.filter((m) => playing.has(m.id));
  for (const m of [
    ...inPlayMoves.filter((x) => !unlocked.has(x.id)),
    ...inPlayMoves.filter((x) => unlocked.has(x.id)),
  ]) {
    const owned = unlocked.has(m.id);
    push({ move: m, owned, needed: !owned, shelf: gestureShelf(level, m.id), tab: 'moves' });
  }

  // 2. la connaissance de la question
  const shelf = savoirShelfForSheet(level, predLines, cardIds);
  if (shelf) {
    const got = unlocked.has(shelf.moveId);
    const ids = got ? [shelf.moveId] : [shelf.moveId, ...shelf.alsoIds];
    // un ordre qui ne trahit pas la bonne : tri par nom
    const list = ids
      .map((id) => MOVES.find((m) => m.id === id))
      .filter((m): m is MoveDef => !!m)
      .sort((a, b) => (got ? 0 : a.name.localeCompare(b.name, 'fr')));
    for (const m of list)
      push({ move: m, owned: unlocked.has(m.id), needed: false, shelf, tab: 'moves' });
  }

  // 3. les autres gestes acquis
  for (const m of gestures)
    if (unlocked.has(m.id))
      push({ move: m, owned: true, needed: false, shelf: gestureShelf(level, m.id), tab: 'moves' });

  // 4. les connaissances acquises, à relire
  for (const m of available(level, MOVES))
    if (unlocked.has(m.id))
      push({
        move: m,
        owned: true,
        needed: false,
        shelf: { question: SAVOIR_QUESTION, moveId: m.id, alsoIds: [] },
        tab: 'cours',
      });
  return out;
}

/**
 * Les gestes que la trousse peut proposer à ce niveau : seuls eux se verrouillent. Un geste d'un niveau
 * au-dessus (« Factoriser » en 6e) n'est jamais proposé — le cacher le perdrait pour toujours. PUR.
 */
export function lockableGestures(level: string | null | undefined): string[] {
  return available(level, GESTURE_MOVES).map((m) => m.id);
}

/** Le geste à débloquer maintenant (celui que la main montre), ou null. PUR. */
export function neededGesture(entries: readonly PlayerMoveEntry[]): MoveDef | null {
  return entries.find((e) => e.needed)?.move ?? null;
}

// ── ce que l'appareil a débloqué, SANS compte ───────────────────────────────────
// Un compte garde ses Moves en base (`user_moves`, migration 0750) ; un visiteur ou une session
// anonyme, sur l'appareil.

const STORE_PREFIX = 'jr_moves_v1:';

export function readUnlockedMoves(userId: string | null | undefined): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORE_PREFIX + (userId ?? 'anon'));
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeUnlockedMoves(userId: string | null | undefined, ids: ReadonlySet<string>) {
  try {
    window.localStorage.setItem(STORE_PREFIX + (userId ?? 'anon'), JSON.stringify([...ids]));
  } catch {
    /* stockage refusé : les Moves restent acquis le temps de la page */
  }
}
